import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { TranslationProvider } from '../contexts/TranslationContext';
import { FullPageLoader } from './FullPageLoader';
import { LandingPage } from './LandingPage';
import { authSessionPresenceService } from '../services/authSessionPresence';
import { queryParams } from '../utils/queryParams';
import {
  isAssistantAdrRoute,
  isConsultantAgentRoute,
  isTransportAnalyzerRoute,
  isWasteOperatorsRoute,
  isAiReliabilityRoute,
  isSecurityTrustRoute,
  isVideoWatchRoute,
  isCookiePolicyRoute,
  isLoginRoute,
  isManifestoRoute,
  isPrivacyPolicyRoute,
  isRegisterRoute,
  isPaymentLinkRoute,
  isKnownPublicRoute,
  isPublicHomeRoute,
  isPublicShareRoute,
  isTermsOfServiceRoute,
  localizePublicHref,
  navigateToLoginRoute,
  navigateToRegisterRoute,
  readPublicPageLanguage,
  readOfferId,
  replaceWithHomeRoute,
} from '../utils/publicRoutes';
import { captureLoginRoutePageView } from '../services/analytics/loginRoutePageView';
import {
  captureBillingFunnelStep,
  captureTryForFreeClick,
} from '../services/analytics/lazyAnalytics';
import { readPrerenderedSharedAnswerExamples } from '../utils/landingSharedAnswerExamples';
import { readPrerenderedLandingBillingCatalog } from '../utils/landingBillingCatalog';
import { readPrerenderedPlatformImpact } from '../services/platformImpact';
import { hasAuditorModuleQuery } from '../utils/auditorModuleDeepLink';
import { hasCommunicationPreferencesQuery } from '../utils/communicationPreferencesDeepLink';
import { hasSeoOpportunityQuery, rememberSeoOpportunityIntent } from '../utils/seoOpportunityDeepLink';
import { saveLanguagePreference } from '../utils/languagePreference';
import type { BillingSubscriptionPriceKey } from '../types/billing';
import {
  clearBillingPlanSelection,
  saveBillingPlanSelection,
  readPaymentLinkAuthLanguageSelection,
} from '../utils/billingPlanSelection';
import { QueryProvider } from '../providers/QueryProvider';
import { CookieInformationBanner } from './CookieInformationBanner';
import {
  normalizeSharedAnswerLanguage,
  readEmbeddedSharedAnswer,
} from '../utils/sharedAnswerMetadata';

const AuthenticatedAppShell = lazy(() =>
  import('./AuthenticatedAppShell').then((module) => ({
    default: module.AuthenticatedAppShell,
  })),
);
const ManifestoPage = lazy(() => import('./ManifestoPage').then((module) => ({
  default: module.ManifestoPage,
})));
const AssistantAdrPage = lazy(() => import('./AssistantAdrPage').then((module) => ({
  default: module.AssistantAdrPage,
})));
const ConsultantAgentPage = lazy(() => import('./ConsultantAgentPage').then((module) => ({
  default: module.ConsultantAgentPage,
})));
const TransportAnalyzerPage = lazy(() => import('./TransportAnalyzerPage').then((module) => ({
  default: module.TransportAnalyzerPage,
})));
const AiReliabilityPage = lazy(() => import('./AiReliabilityPage').then((module) => ({
  default: module.AiReliabilityPage,
})));
const WasteOperatorsPage = lazy(() => import('./WasteOperatorsPage').then((module) => ({
  default: module.WasteOperatorsPage,
})));
const SecurityTrustPage = lazy(() => import('./SecurityTrustPage').then((module) => ({
  default: module.SecurityTrustPage,
})));
const VideoWatchPage = lazy(() => import('./VideoWatchPage').then((module) => ({
  default: module.VideoWatchPage,
})));
const PaymentLinkRoute = lazy(() => import('./PaymentLinkRoute').then((module) => ({
  default: module.PaymentLinkRoute,
})));
const OfferPage = lazy(() => import('./billing/OfferPage').then((module) => ({
  default: module.OfferPage,
})));
const NotFoundPage = lazy(() => import('./NotFoundPage').then((module) => ({
  default: module.NotFoundPage,
})));
const PublicSharedAnswerRoute = lazy(() => import('./PublicSharedAnswerRoute').then((module) => ({
  default: module.PublicSharedAnswerRoute,
})));
const TermsOfServicePage = lazy(() => import('./TermsOfServicePage').then((module) => ({
  default: module.TermsOfServicePage,
})));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage').then((module) => ({
  default: module.PrivacyPolicyPage,
})));
const CookiePolicyPage = lazy(() => import('./CookiePolicyPage').then((module) => ({
  default: module.CookiePolicyPage,
})));
interface AppShellState {
  shouldShow: boolean;
  shouldNavigateToApp: boolean;
  shouldSkipSessionRefresh: boolean;
}

function shouldOpenLoginDirectly() {
  return isLoginRoute() || isRegisterRoute() || queryParams.get('admin') === '1';
}

function shouldOpenMagicLinkVerification() {
  return queryParams.has('sessionId') && queryParams.has('token');
}

function createInitialAppShellState(): AppShellState {
  const hasVerificationParams = shouldOpenMagicLinkVerification();
  const shouldOpenLogin = shouldOpenLoginDirectly();
  const shouldOpenAuditor = hasAuditorModuleQuery();
  const shouldOpenPreferences = hasCommunicationPreferencesQuery();
  const shouldOpenSeoOpportunity = hasSeoOpportunityQuery();
  const hasSessionHint = authSessionPresenceService.hasActiveSessionHint();

  return {
    shouldShow: hasVerificationParams || shouldOpenLogin || shouldOpenAuditor || shouldOpenPreferences || shouldOpenSeoOpportunity || hasSessionHint,
    shouldNavigateToApp: shouldOpenLogin || shouldOpenAuditor || shouldOpenPreferences || shouldOpenSeoOpportunity,
    shouldSkipSessionRefresh: false,
  };
}

function createLoginAppShellState(): AppShellState {
  return {
    shouldShow: true,
    shouldNavigateToApp: true,
    shouldSkipSessionRefresh: true,
  };
}

function useCanonicalPublicHomeRoute(shouldShowPublicLanding: boolean) {
  useEffect(() => {
    if (!shouldShowPublicLanding || isPublicHomeRoute()) {
      return;
    }

    replaceWithHomeRoute();
  }, [shouldShowPublicLanding]);
}

function usePublicLandingIndexingMetadata(
  shouldShowPublicLanding: boolean,
  language: 'en' | 'fr',
) {
  useEffect(() => {
    if (!shouldShowPublicLanding) return;

    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (robotsMeta) {
      robotsMeta.content = 'index,follow,max-image-preview:large';
    }
    if (canonicalLink) {
      canonicalLink.href = language === 'en'
        ? 'https://palen-adr.eu/en/'
        : 'https://palen-adr.eu/';
    }
  }, [language, shouldShowPublicLanding]);
}

function useBrowserNavigationSync(syncAppShellState: () => void) {
  useEffect(() => {
    window.addEventListener('popstate', syncAppShellState);
    return () => window.removeEventListener('popstate', syncAppShellState);
  }, [syncAppShellState]);
}

function TranslatedRoute({
  children,
  initialLanguage,
  localizeLanguageInUrl = false,
  useSavedLanguage,
}: {
  children: ReactNode;
  initialLanguage?: 'en' | 'fr';
  localizeLanguageInUrl?: boolean;
  useSavedLanguage?: boolean;
}) {
  const navigateToLanguage = useCallback((language: 'en' | 'fr') => {
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const localizedHref = localizePublicHref(currentHref, language);
    if (localizedHref !== currentHref) {
      window.location.assign(localizedHref);
    }
  }, []);

  return (
    <TranslationProvider
      initialLanguage={initialLanguage}
      onLanguageChange={localizeLanguageInUrl ? navigateToLanguage : undefined}
      useSavedLanguage={useSavedLanguage}
    >
      {children}
      <CookieInformationBanner />
    </TranslationProvider>
  );
}

export function RootRoute() {
  useEffect(() => rememberSeoOpportunityIntent(), []);
  const [appShellState, setAppShellState] = useState(() => createInitialAppShellState());
  const shouldShowPublicShare = isPublicShareRoute();
  const shouldShowManifesto = isManifestoRoute();
  const shouldShowAssistantAdr = isAssistantAdrRoute();
  const shouldShowConsultantAgent = isConsultantAgentRoute();
  const shouldShowTransportAnalyzer = isTransportAnalyzerRoute();
  const shouldShowWasteOperators = isWasteOperatorsRoute();
  const shouldShowAiReliability = isAiReliabilityRoute();
  const shouldShowSecurityTrust = isSecurityTrustRoute();
  const shouldShowVideoWatch = isVideoWatchRoute();
  const shouldShowPaymentLink = isPaymentLinkRoute();
  const offerId = readOfferId();
  const shouldShowTermsOfService = isTermsOfServiceRoute();
  const shouldShowPrivacyPolicy = isPrivacyPolicyRoute();
  const shouldShowCookiePolicy = isCookiePolicyRoute();
  const shouldShowNotFound = !isKnownPublicRoute();
  const shouldShowPublicLanding = isPublicHomeRoute() && !appShellState.shouldShow;
  const publicPageLanguage = readPublicPageLanguage();
  const paymentLinkAuthLanguage = readPaymentLinkAuthLanguageSelection();
  const syncAppShellState = useCallback(() => setAppShellState(createInitialAppShellState()), []);
  const openLogin = () => {
    const previousPathname = window.location.pathname;
    navigateToLoginRoute();
    captureLoginRoutePageView({
      page: 'login',
      source: 'public_landing_header_login',
      previousPathname,
    });
    setAppShellState(createLoginAppShellState());
  };
  const openRegister = (priceKey?: BillingSubscriptionPriceKey) => {
    const previousPathname = window.location.pathname;
    if (priceKey) saveBillingPlanSelection(priceKey);
    else clearBillingPlanSelection();
    if (priceKey) {
      void captureBillingFunnelStep({
        step: 'plan_selected',
        source: 'public_landing_pricing',
        priceKey,
        funnelType: 'initial_subscription',
      });
    }
    void captureTryForFreeClick();
    navigateToRegisterRoute();
    captureLoginRoutePageView({
      page: 'register',
      source: 'public_landing_cta',
      previousPathname,
    });
    setAppShellState(createLoginAppShellState());
  };
  const openOfferPurchase = (priceKey: BillingSubscriptionPriceKey) => {
    saveLanguagePreference(publicPageLanguage);
    if (!authSessionPresenceService.hasActiveSessionHint()) {
      openRegister(priceKey);
      return;
    }
    saveBillingPlanSelection(priceKey);
    navigateToRegisterRoute();
    setAppShellState({ shouldShow: true, shouldNavigateToApp: true, shouldSkipSessionRefresh: false });
  };

  useCanonicalPublicHomeRoute(shouldShowPublicLanding);
  usePublicLandingIndexingMetadata(shouldShowPublicLanding, publicPageLanguage);
  useBrowserNavigationSync(syncAppShellState);

  if (shouldShowPublicLanding) {
    return (
      <TranslatedRoute
        initialLanguage={publicPageLanguage}
        localizeLanguageInUrl
        useSavedLanguage={false}
      >
        <QueryProvider>
          <LandingPage
            onGetStarted={openRegister}
            onSignIn={openLogin}
            initiallyVisible
            initialQuestionExamples={readPrerenderedSharedAnswerExamples()}
            initialBillingCatalog={readPrerenderedLandingBillingCatalog()}
            initialPlatformImpact={readPrerenderedPlatformImpact()}
          />
        </QueryProvider>
      </TranslatedRoute>
    );
  }

  if (shouldShowManifesto) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <ManifestoPage
            onGetStarted={() => openRegister()}
            onSignIn={openLogin}
          />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowAssistantAdr) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <AssistantAdrPage
            onGetStarted={() => openRegister()}
            onSignIn={openLogin}
          />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowConsultantAgent) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <QueryProvider>
            <ConsultantAgentPage
              initialQuestionExamples={readPrerenderedSharedAnswerExamples()}
              initialPlatformImpact={readPrerenderedPlatformImpact()}
              onGetStarted={() => openRegister()}
              onSignIn={openLogin}
            />
          </QueryProvider>
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowTransportAnalyzer) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <QueryProvider>
            <TransportAnalyzerPage
              initialPlatformImpact={readPrerenderedPlatformImpact()}
              onGetStarted={() => openRegister()}
              onSignIn={openLogin}
            />
          </QueryProvider>
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowWasteOperators) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <WasteOperatorsPage
            onGetStarted={() => openRegister()}
            onSignIn={openLogin}
          />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowAiReliability) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <QueryProvider>
            <AiReliabilityPage
              initialPlatformImpact={readPrerenderedPlatformImpact()}
              onGetStarted={() => openRegister()}
              onSignIn={openLogin}
            />
          </QueryProvider>
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowSecurityTrust) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <SecurityTrustPage
            onSignIn={openLogin}
          />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowVideoWatch) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} useSavedLanguage={false}>
          <VideoWatchPage language={publicPageLanguage} onGetStarted={() => openRegister()} />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (offerId) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <QueryProvider>
            <OfferPage plan={offerId === '1' ? 'pro' : 'enterprise'} onSelectPlan={openOfferPurchase} />
          </QueryProvider>
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowPaymentLink) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <PaymentLinkRoute />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowNotFound) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage}>
          <NotFoundPage />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowTermsOfService) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <TermsOfServicePage />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowPrivacyPolicy) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <PrivacyPolicyPage />
        </TranslatedRoute>
      </Suspense>
    );
  }

  if (shouldShowCookiePolicy) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <TranslatedRoute initialLanguage={publicPageLanguage} localizeLanguageInUrl useSavedLanguage={false}>
          <CookiePolicyPage />
        </TranslatedRoute>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      {shouldShowPublicShare ? (
        <TranslatedRoute
          initialLanguage={normalizeSharedAnswerLanguage(readEmbeddedSharedAnswer()?.locale ?? null)}
          useSavedLanguage={false}
        >
          <PublicSharedAnswerRoute />
        </TranslatedRoute>
      ) : (
        <TranslatedRoute initialLanguage={paymentLinkAuthLanguage ?? undefined} useSavedLanguage={paymentLinkAuthLanguage === null}>
          <AuthenticatedAppShell
            initialNavigateToApp={appShellState.shouldNavigateToApp}
            skipInitialSessionRefresh={appShellState.shouldSkipSessionRefresh}
          />
        </TranslatedRoute>
      )}
    </Suspense>
  );
}
